import type { ProductTypeDto } from '../api/productTypes'

const normalizeSearchText = (value: string): string =>
  value.trim().toLocaleLowerCase('ru-RU').replaceAll('ё', 'е')

export const formatProductTypeLabel = (
  productTypeId: number | null,
  productTypeName: string | null | undefined,
  productTypeActive: boolean | null | undefined,
  typeNames: Map<number, string>,
): string => {
  if (productTypeId == null) {
    return '—'
  }
  const name = productTypeName ?? typeNames.get(productTypeId)
  if (!name) {
    return `#${productTypeId}`
  }
  if (productTypeActive === false) {
    return `${name} (выкл.)`
  }
  return name
}

export const isL0ProductType = (type: ProductTypeDto): boolean =>
  type.parentId == null && type.active !== false && Boolean(type.code?.trim())

export const productTypeLevel = (type: ProductTypeDto): number => {
  if (!type.code?.trim()) {
    return -1
  }
  return type.code.split('.').length - 1
}

export const listChildProductTypes = (
  parent: ProductTypeDto,
  productTypes: ProductTypeDto[],
  childLevel: number,
): ProductTypeDto[] =>
  productTypes.filter(
    (type) => productTypeLevel(type) === childLevel && type.code.startsWith(`${parent.code}.`),
  )

export type ProductTypeCascadeSelection = {
  l0Id: number | null
  l1Id: number | null
  l2Id: number | null
  l3Id: number | null
}

export const productTypeAncestorAtLevel = (
  type: ProductTypeDto,
  productTypes: ProductTypeDto[],
  targetLevel: number,
): ProductTypeDto | null => {
  if (!type.code) {
    return productTypeLevel(type) === targetLevel ? type : null
  }
  const segments = type.code.split('.')
  if (targetLevel >= segments.length) {
    return null
  }
  const ancestorCode = segments.slice(0, targetLevel + 1).join('.')
  return productTypes.find((item) => item.code === ancestorCode) ?? null
}

export const resolveProductTypeCascade = (
  productTypeId: number | null,
  productTypes: ProductTypeDto[],
): ProductTypeCascadeSelection => {
  if (productTypeId == null) {
    return { l0Id: null, l1Id: null, l2Id: null, l3Id: null }
  }
  const selected = productTypes.find((type) => type.id === productTypeId) ?? null
  if (!selected) {
    return { l0Id: null, l1Id: null, l2Id: null, l3Id: null }
  }
  const level = productTypeLevel(selected)
  const l0 = productTypeAncestorAtLevel(selected, productTypes, 0)
  const l1 = level >= 1 ? productTypeAncestorAtLevel(selected, productTypes, 1) : null
  const l2 = level >= 2 ? productTypeAncestorAtLevel(selected, productTypes, 2) : null
  return {
    l0Id: l0?.id ?? (level === 0 ? selected.id : null),
    l1Id: l1?.id ?? (level === 1 ? selected.id : null),
    l2Id: l2?.id ?? (level === 2 ? selected.id : null),
    l3Id: level >= 3 ? selected.id : null,
  }
}

export const effectiveProductTypeIdFromCascade = (
  cascade: ProductTypeCascadeSelection,
): number | null => cascade.l3Id ?? cascade.l2Id ?? cascade.l1Id ?? cascade.l0Id ?? null

export const expandProductTypeToDescendantIds = (
  productTypeId: number,
  productTypes: ProductTypeDto[],
): number[] => {
  const selected = productTypes.find((type) => type.id === productTypeId)
  if (!selected) {
    return [productTypeId]
  }
  const byParent = new Map<number, ProductTypeDto[]>()
  for (const type of productTypes) {
    if (type.parentId != null) {
      const siblings = byParent.get(type.parentId) ?? []
      siblings.push(type)
      byParent.set(type.parentId, siblings)
    }
  }
  const ids = new Set<number>([selected.id])
  const prefix = `${selected.code}.`
  for (const type of productTypes) {
    if (type.id === selected.id || type.code.startsWith(prefix)) {
      ids.add(type.id)
    }
  }
  const stack = [selected.id]
  while (stack.length > 0) {
    const parentId = stack.pop()!
    for (const child of byParent.get(parentId) ?? []) {
      if (ids.has(child.id)) {
        continue
      }
      ids.add(child.id)
      stack.push(child.id)
    }
  }
  return [...ids]
}

export const filterSuggestions = (options: string[], query: string, limit = 80): string[] => {
  const normalized = normalizeSearchText(query)
  if (!normalized) {
    return options.slice(0, limit)
  }
  return options
    .filter((option) => normalizeSearchText(option).includes(normalized))
    .slice(0, limit)
}

export const findProductTypeByName = (
  types: ProductTypeDto[],
  query: string,
): ProductTypeDto | null => {
  const normalized = normalizeSearchText(query)
  if (!normalized) {
    return null
  }
  return types.find((type) => normalizeSearchText(type.name) === normalized) ?? null
}
